import { Form, InputNumber, Modal } from 'antd';
import DatePicker from '~UI/DatePicker';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { investorIncomeTrustsDivestmentRequestsAddAction } from '~Investments/actions';
import dayjs, { Dayjs } from 'dayjs';
import IInvestorAccountIncomeTrust from '~Api/IncomeTrust/IInvestorAccountIncomeTrust';
import { currencyFormatter } from '~utilities/formatters';
import constants from '~constants';
import IInvestorIncomeTrustDivestmentRequest from '~Api/IncomeTrust/IInvestorIncomeTrustDivestmentRequest';
import classTypeLabels from '~Api/IncomeTrust/classTypeLabels';

interface IInvestorIncomeTrustDivestmentRequestAddError {
    amount?: string;
    divestmentDate?: string;
    requestedDate?: string;
}

export default function InvestorIncomeTrustDivestmentRequestAddModal(props: {
    investorAccountIncomeTrust: IInvestorAccountIncomeTrust;
    onClose: () => void;
    isOpen: boolean;
}): ReactElement {
    const { investorAccountIncomeTrust, onClose, isOpen } = props;

    const dispatch: Dispatch = useDispatch();

    const [ errors, setErrors ] = useState<IInvestorIncomeTrustDivestmentRequestAddError>({});
    const [ amount, setAmount ] = useState<number>(null);
    const [ divestmentDate, setDivestmentDate ] = useState<string>(null);
    const [ requestedDate, setRequestedDate ] = useState<string>(null);

    const validateAmount: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!amount) {
            error = 'Please enter amount';
        } else if (amount <= 0) {
            error = 'Amount must be greater than $0';
        } else if (amount > investorAccountIncomeTrust.principalBalance) {
            error = 'Amount exceeds investor\'s current investment';
        }

        setErrors((prevErrors: IInvestorIncomeTrustDivestmentRequestAddError): IInvestorIncomeTrustDivestmentRequestAddError => ({
            ...prevErrors,
            amount: error,
        }));

        return !error;
    }, [
        amount,
        investorAccountIncomeTrust,
    ]);

    const validateDivestmentDate: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!divestmentDate) {
            error = 'Please enter divestment date';
        } else if (dayjs(divestmentDate).isBefore(dayjs(), 'day')) {
            error = 'Divestment date cannot be in the past';
        }

        setErrors((prevErrors: IInvestorIncomeTrustDivestmentRequestAddError): IInvestorIncomeTrustDivestmentRequestAddError => ({
            ...prevErrors,
            divestmentDate: error,
        }));

        return !error;
    }, [
        divestmentDate,
    ]);

    const validateRequestedDate: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!requestedDate) {
            error = 'Please enter requested date';
        } else if (dayjs(requestedDate).isAfter(dayjs(), 'day')) {
            error = 'Requested date cannot be in the future';
        }

        setErrors((prevErrors: IInvestorIncomeTrustDivestmentRequestAddError): IInvestorIncomeTrustDivestmentRequestAddError => ({
            ...prevErrors,
            requestedDate: error,
        }));

        return !error;
    }, [
        requestedDate,
    ]);

    const onChangeAmount: (value: number) => void = useCallback((value: number): void => {
        setAmount(value);
    }, []);

    const onChangeDivestmentDate: (value: Dayjs) => void = useCallback((value: Dayjs): void => {
        setDivestmentDate(value ? value.format('YYYY-MM-DD') : null);
    }, []);

    const onChangeRequestedDate: (value: Dayjs) => void = useCallback((value: Dayjs): void => {
        setRequestedDate(value ? value.format('YYYY-MM-DD') : null);
    }, []);

    const onClickOk: () => void = useCallback((): void => {
        let valid: boolean = true;

        valid = validateRequestedDate() && valid;
        valid = validateDivestmentDate() && valid;
        valid = validateAmount() && valid;

        if (!valid) {
            return;
        }

        const requestedTime: string = dayjs(requestedDate).format(constants.DATE_ISO_FORMAT);
        const investorIncomeTrustDivestmentRequest: IInvestorIncomeTrustDivestmentRequest = {
            amount,
            divestmentDate,
            investorAccountIncomeTrustUuid: investorAccountIncomeTrust.uuid,
            requestedTime,
        };
        dispatch(investorIncomeTrustsDivestmentRequestsAddAction(investorIncomeTrustDivestmentRequest));
        setAmount(null);
        setDivestmentDate(null);
        setRequestedDate(null);
        setErrors({});
        onClose();
    }, [
        amount,
        dispatch,
        divestmentDate,
        investorAccountIncomeTrust,
        requestedDate,
        onClose,
        validateRequestedDate,
        validateDivestmentDate,
        validateAmount,
    ]);

    const disabledRequestedDate: (date: Dayjs) => boolean = useCallback((date: Dayjs): boolean => {
        return dayjs(date).isAfter(dayjs(), 'day');
    }, []);

    const disabledDivestmentDate: (date: Dayjs) => boolean = useCallback((date: Dayjs): boolean => {
        return dayjs(date).isBefore(dayjs(), 'day');
    }, []);

    return (
        <Modal
            onCancel={onClose}
            onOk={onClickOk}
            open={isOpen}
            title={`${classTypeLabels[investorAccountIncomeTrust.classType]} Schedule Divestment`}
            wrapClassName='investor-income-trust-divestment-request-add-modal'
        >
            <Form.Item
                className='requested-date'
                label='Requested Date'
                help={errors.requestedDate}
                validateStatus={errors.requestedDate && 'error'}
            >
                <DatePicker
                    disabledDate={disabledRequestedDate}
                    onChange={onChangeRequestedDate}
                    format='DD/MM/YYYY'
                    value={requestedDate ? dayjs(requestedDate) : null}
                    onBlur={validateRequestedDate}
                />
            </Form.Item>
            <Form.Item
                className='divestment-date'
                label='Divestment Date'
                help={errors.divestmentDate}
                validateStatus={errors.divestmentDate && 'error'}
            >
                <DatePicker
                    disabledDate={disabledDivestmentDate}
                    onChange={onChangeDivestmentDate}
                    format='DD/MM/YYYY'
                    value={divestmentDate ? dayjs(divestmentDate) : null}
                    onBlur={validateDivestmentDate}
                />
            </Form.Item>
            <Form.Item
                className='amount'
                extra={currencyFormatter.format(amount)}
                label='Amount'
                help={errors.amount}
                validateStatus={errors.amount && 'error'}
            >
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeAmount}
                    onBlur={validateAmount}
                    value={amount}
                />
            </Form.Item>
        </Modal>
    );
}
